import type { RentalRequest } from '../types';

type RentalRequestPayment = {
  rental_request_id: string;
  amount: number | null;
  payment_status: string | null;
  payment_method: string | null;
  paid_at: string | null;
  created_at: string | null;
};

const REVENUE_STATUSES: RentalRequest['status'][] = [
  'confirmed',
  'awaiting_payment',
  'paid',
  'rented_out',
  'completed',
];

/** Order där pengar inte längre ska räknas som kvar hos er (t.ex. full/partiell återbetalning). */
export function isPaymentRefundedForStats(request: RentalRequest): boolean {
  const ps = String((request as any).payment_status ?? '')
    .trim()
    .toLowerCase();
  return ps === 'refunded' || ps === 'partially_refunded';
}

/**
 * Bokningar som ska ingå i intäkts-/likvidstatistik: vanliga genomförandestatusar,
 * samt avbruten men fortfarande betald (kund avbokade försent — ingen återbetalning).
 * Återbetalda ordrar räknas inte in.
 */
export function countsTowardRevenueStats(request: RentalRequest): boolean {
  if (request.archived) return false;
  if (isPaymentRefundedForStats(request)) return false;
  const st = String(request.status || '') as RentalRequest['status'];
  if (REVENUE_STATUSES.includes(st)) return true;
  if (st === 'cancelled' && String((request as any).payment_status ?? '').trim().toLowerCase() === 'paid') {
    return true;
  }
  return false;
}

function toDate(value: string | null | undefined): Date | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isInMonth(date: Date | null, year: number, month: number): boolean {
  if (!date) return false;
  return date.getFullYear() === year && date.getMonth() === month;
}

function addCalendarDays(date: Date, days: number): Date {
  const copy = new Date(date);
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function addBankDays(date: Date, days: number): Date {
  const copy = new Date(date);
  let remaining = days;
  while (remaining > 0) {
    copy.setDate(copy.getDate() + 1);
    const day = copy.getDay();
    if (day !== 0 && day !== 6) {
      remaining -= 1;
    }
  }
  return copy;
}

function getOrderTotal(request: RentalRequest): number {
  return (Number(request.total_amount) || 0) + Number((request as any).delivery_fee || 0);
}

function getPaidPaymentRows(
  request: RentalRequest,
  paymentRows: RentalRequestPayment[],
): RentalRequestPayment[] {
  const rows = paymentRows.filter((p) => p.rental_request_id === request.id);
  return rows.filter((p) => String(p.payment_status || '').toLowerCase() === 'completed');
}

type LiquidityBreakdown = {
  liquidReceived: number;
  pendingPayout: number;
};

function calculateLiquidityBreakdown(
  request: RentalRequest,
  paymentRows: RentalRequestPayment[],
  now: Date,
): LiquidityBreakdown {
  const total = getOrderTotal(request);
  const bookingSource = String((request as any).booking_source || '').toLowerCase();
  const paymentMethod = String((request as any).payment_method || '').toLowerCase();
  const isMarkedPaid = String((request as any).payment_status || '').toLowerCase() === 'paid';

  if (total <= 0) {
    return { liquidReceived: 0, pendingPayout: 0 };
  }

  // Hygglo: utbetalning uppskattas när hyresperioden är slut + 3 dagar.
  if (bookingSource === 'hygglo' && isMarkedPaid) {
    const endDate = toDate(request.event_end_date || request.event_date || request.created_at);
    if (!endDate) {
      return { liquidReceived: 0, pendingPayout: total };
    }
    const payoutDate = addCalendarDays(endDate, 3);
    return payoutDate <= now
      ? { liquidReceived: total, pendingPayout: 0 }
      : { liquidReceived: 0, pendingPayout: total };
  }

  const paidRows = getPaidPaymentRows(request, paymentRows);
  const stripePaidRows = paidRows.filter((row) => {
    const rowMethod = String(row.payment_method || '').toLowerCase();
    return rowMethod === 'stripe' || paymentMethod === 'stripe';
  });

  if (stripePaidRows.length > 0) {
    const settled = stripePaidRows.reduce((acc, row) => {
      const amount = Number(row.amount) || 0;
      const paidAt = toDate(row.paid_at || row.created_at);
      if (!paidAt) return acc;
      const expectedPayoutDate = addBankDays(paidAt, 5);
      if (expectedPayoutDate <= now) {
        return acc + amount;
      }
      return acc;
    }, 0);

    const totalStripePaid = stripePaidRows.reduce((acc, row) => acc + (Number(row.amount) || 0), 0);
    const pendingByDelay = Math.max(0, totalStripePaid - settled);

    return {
      liquidReceived: Math.min(settled, total),
      pendingPayout: Math.min(total, pendingByDelay),
    };
  }

  if (isMarkedPaid) {
    return { liquidReceived: total, pendingPayout: 0 };
  }

  return { liquidReceived: 0, pendingPayout: 0 };
}

export type MonthlyLiquidityMetrics = {
  bookingsCount: number;
  totalTakenRevenue: number;
  paidRevenue: number;
  unpaidTakenRevenue: number;
  liquidReceived: number;
  pendingPayout: number;
  expectedLiquidity: number;
};

export function calculateMonthlyLiquidityMetrics(params: {
  requests: RentalRequest[];
  paymentRows: RentalRequestPayment[];
  year: number;
  month: number;
  now?: Date;
}): MonthlyLiquidityMetrics {
  const { requests, paymentRows, year, month, now = new Date() } = params;

  const inMonth = requests
    .filter((r) => countsTowardRevenueStats(r))
    .filter((r) => isInMonth(toDate(r.created_at), year, month));

  const paidRevenue = inMonth
    .filter((r) => String((r as any).payment_status || '').toLowerCase() === 'paid')
    .reduce((sum, r) => sum + getOrderTotal(r), 0);
  const totalTakenRevenue = inMonth.reduce((sum, r) => sum + getOrderTotal(r), 0);
  const unpaidTakenRevenue = Math.max(0, totalTakenRevenue - paidRevenue);

  const liquidity = inMonth.reduce(
    (acc, request) => {
      const breakdown = calculateLiquidityBreakdown(request, paymentRows, now);
      return {
        liquidReceived: acc.liquidReceived + breakdown.liquidReceived,
        pendingPayout: acc.pendingPayout + breakdown.pendingPayout,
      };
    },
    { liquidReceived: 0, pendingPayout: 0 },
  );

  return {
    bookingsCount: inMonth.length,
    totalTakenRevenue,
    paidRevenue,
    unpaidTakenRevenue,
    liquidReceived: liquidity.liquidReceived,
    pendingPayout: liquidity.pendingPayout,
    expectedLiquidity: liquidity.liquidReceived + liquidity.pendingPayout,
  };
}

export type MonthlySeriesRange = {
  fromYear: number;
  fromMonth: number; // 0-indexed
  toYear: number;
  toMonth: number; // 0-indexed
};

function monthKey(year: number, month: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}`;
}

function compareYearMonth(aYear: number, aMonth: number, bYear: number, bMonth: number): number {
  if (aYear !== bYear) return aYear - bYear;
  return aMonth - bMonth;
}

/** Inclusive month range as chronologically ordered keys. Caps at 24 months. */
export function buildMonthKeysInRange(range: MonthlySeriesRange): string[] {
  let { fromYear, fromMonth, toYear, toMonth } = range;
  if (compareYearMonth(fromYear, fromMonth, toYear, toMonth) > 0) {
    [fromYear, fromMonth, toYear, toMonth] = [toYear, toMonth, fromYear, fromMonth];
  }

  const keys: string[] = [];
  let y = fromYear;
  let m = fromMonth;
  while (compareYearMonth(y, m, toYear, toMonth) <= 0 && keys.length < 24) {
    keys.push(monthKey(y, m));
    m += 1;
    if (m > 11) {
      m = 0;
      y += 1;
    }
  }
  return keys;
}

export function resolveMonthlySeriesRange(params: {
  now?: Date;
  monthsCount?: number;
  fromYear?: number;
  fromMonth?: number;
  toYear?: number;
  toMonth?: number;
}): MonthlySeriesRange {
  const {
    now = new Date(),
    monthsCount,
    fromYear,
    fromMonth,
    toYear,
    toMonth,
  } = params;

  if (
    fromYear != null &&
    fromMonth != null &&
    toYear != null &&
    toMonth != null
  ) {
    return { fromYear, fromMonth, toYear, toMonth };
  }

  const count = Math.max(1, Math.min(monthsCount ?? 6, 24));
  const end = new Date(now.getFullYear(), now.getMonth(), 1);
  const start = new Date(end.getFullYear(), end.getMonth() - (count - 1), 1);
  return {
    fromYear: start.getFullYear(),
    fromMonth: start.getMonth(),
    toYear: end.getFullYear(),
    toMonth: end.getMonth(),
  };
}

export function calculateMonthlyLiquiditySeries(params: {
  requests: RentalRequest[];
  paymentRows: RentalRequestPayment[];
  now?: Date;
  monthsCount?: number;
  fromYear?: number;
  fromMonth?: number;
  toYear?: number;
  toMonth?: number;
}): Array<[string, number]> {
  const { requests, paymentRows, now = new Date() } = params;
  const range = resolveMonthlySeriesRange(params);
  const keys = buildMonthKeysInRange(range);
  const monthMap: Record<string, number> = Object.fromEntries(keys.map((k) => [k, 0]));

  requests
    .filter((r) => countsTowardRevenueStats(r))
    .forEach((request) => {
      const created = toDate(request.created_at);
      if (!created) return;
      const key = monthKey(created.getFullYear(), created.getMonth());
      if (!(key in monthMap)) return;
      const metrics = calculateMonthlyLiquidityMetrics({
        requests: [request],
        paymentRows,
        year: created.getFullYear(),
        month: created.getMonth(),
        now,
      });
      monthMap[key] = (monthMap[key] || 0) + metrics.totalTakenRevenue;
    });

  return keys.map((key) => [key, monthMap[key] || 0]);
}

/** @deprecated Prefer calculateMonthlyLiquiditySeries */
export function calculateLatestSixMonthsLiquidity(params: {
  requests: RentalRequest[];
  paymentRows: RentalRequestPayment[];
  now?: Date;
}): Array<[string, number]> {
  return calculateMonthlyLiquiditySeries({ ...params, monthsCount: 6 });
}

export function deliveryFeeCountsTowardStats(request: RentalRequest): boolean {
  if (!countsTowardRevenueStats(request)) return false;
  const t = String((request as any).delivery_option_type ?? request.delivery_option ?? '').toLowerCase();
  if (t !== 'delivery' && t !== 'delivery_with_installation') return false;
  const fee = Number((request as any).delivery_fee ?? 0) || 0;
  return fee > 0;
}

function deliveryMonthDate(request: RentalRequest): Date | null {
  return (
    toDate((request as any).event_date) ||
    toDate((request as any).start_date) ||
    toDate((request as any).created_at) ||
    null
  );
}

export function getDeliveryMonthKeyByEventDate(request: RentalRequest): string | null {
  const d = deliveryMonthDate(request);
  if (!d) return null;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function calculateMonthlyDeliveryFeesByEventDateSeries(params: {
  requests: RentalRequest[];
  now?: Date;
  monthsCount?: number;
  fromYear?: number;
  fromMonth?: number;
  toYear?: number;
  toMonth?: number;
}): Array<[string, number]> {
  const { requests } = params;
  const range = resolveMonthlySeriesRange(params);
  const keys = buildMonthKeysInRange(range);
  const monthMap: Record<string, number> = Object.fromEntries(keys.map((k) => [k, 0]));

  requests
    .filter((r) => deliveryFeeCountsTowardStats(r))
    .forEach((request) => {
      const key = getDeliveryMonthKeyByEventDate(request);
      if (!key || !(key in monthMap)) return;
      const fee = Number((request as any).delivery_fee ?? 0) || 0;
      monthMap[key] = (monthMap[key] || 0) + fee;
    });

  return keys.map((key) => [key, monthMap[key] || 0]);
}

/** @deprecated Prefer calculateMonthlyDeliveryFeesByEventDateSeries */
export function calculateLatestSixMonthsDeliveryFeesByEventDate(params: {
  requests: RentalRequest[];
  now?: Date;
}): Array<[string, number]> {
  return calculateMonthlyDeliveryFeesByEventDateSeries({ ...params, monthsCount: 6 });
}

export function calculateMonthDeliveryFeeTotal(params: {
  requests: RentalRequest[];
  year: number;
  month: number; // 0-indexed
}): number {
  const { requests, year, month } = params;
  return requests
    .filter((r) => deliveryFeeCountsTowardStats(r))
    .reduce((sum, r) => {
      const d = deliveryMonthDate(r);
      if (!d) return sum;
      if (d.getFullYear() !== year || d.getMonth() !== month) return sum;
      return sum + (Number((r as any).delivery_fee ?? 0) || 0);
    }, 0);
}
